/** @jsx jsx */
import React, { useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { timeTrackingFormatter } from '@atlassian/jira-time-tracking-formatter/src/main.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import { ORIGINAL_ESTIMATE, STORY_POINTS } from '../../../../model/constants';
import type { EstimationType } from '../../../../model/types';
import messages from './messages';

const containerStyles = css({
	lineHeight: token('space.200', '16px'),

	fontSize: token('space.150', '12px'),
	padding: token('space.050', '4px'),
});

const containerP = css({
	margin: 0,
});

type Props = {
	username: string;
	storyPoints: number;
	remainingTimeEstimateInSeconds: number;
	issueCount: number;
	isEstimationEnabled: boolean;
	estimationType: EstimationType;
	isRemainingTimeTrackingEnabled: boolean;
	originalTimeEstimateInSeconds: number;
	timeTrackingOptions: TimeTrackingOptions;
	estimationFieldName: string;
};

const TooltipContent = ({
	username,
	storyPoints,
	remainingTimeEstimateInSeconds,
	originalTimeEstimateInSeconds,
	issueCount,
	estimationType,
	isEstimationEnabled,
	isRemainingTimeTrackingEnabled,
	timeTrackingOptions,
	estimationFieldName,
}: Props) => {
	const intl = useIntl();
	const { formatMessage } = intl;
	const isShowTimeEstimateVisible = isEstimationEnabled && estimationType === ORIGINAL_ESTIMATE;
	const isStoryPointsVisible = isEstimationEnabled && estimationType === STORY_POINTS;

	const remainingTimeEstimate = useMemo(
		() => timeTrackingFormatter(remainingTimeEstimateInSeconds, timeTrackingOptions, intl),
		[remainingTimeEstimateInSeconds, timeTrackingOptions, intl],
	);

	const originalTimeEstimate = useMemo(
		() => timeTrackingFormatter(originalTimeEstimateInSeconds, timeTrackingOptions, intl),
		[originalTimeEstimateInSeconds, timeTrackingOptions, intl],
	);

	const originalTimeEstimateField = useMemo(
		() => `${originalTimeEstimate} ${estimationFieldName.toLowerCase()}`,
		[originalTimeEstimate, estimationFieldName],
	);

	const numericEstimateField = useMemo(
		() => `${storyPoints} ${estimationFieldName.toLowerCase()}`,
		[storyPoints, estimationFieldName],
	);

	return (
		// eslint-disable-next-line @atlaskit/design-system/use-primitives
		<div css={containerStyles}>
			<b>{username}</b>
			{isStoryPointsVisible && <p css={containerP}>{numericEstimateField}</p>}
			{isShowTimeEstimateVisible && <p css={containerP}>{originalTimeEstimateField}</p>}
			{isRemainingTimeTrackingEnabled && isEstimationEnabled && (
				<p css={containerP}>
					{formatMessage(messages.remainingTimeEstimate, {
						remainingTimeEstimate,
					})}
				</p>
			)}
			<p css={containerP}>{formatMessage(messages.issueCount, { issueCount })}</p>
		</div>
	);
};

export default TooltipContent;
